import {flowRight, negate} from 'lodash';

export const isInvalidNumber = flowRight(isNaN, parseNumber);
export const isValidNumber = negate(isInvalidNumber);

export const isPositiveInteger = (n: any): boolean => {
  return isValidNumber(n) && isInteger(n) && n > 0;
};

// Is this a decimal number that matches the intuitive format
export function isValidBasicNumber(s: string) {
  if (isValidNumber(s)) {
    const n = Number(s);
    const ns = String(n);
    return !/e/.test(ns) && !/\.$/.test(s);
  } else {
    return false;
  }
}

export function parseNumber(str: number | string) {
  str = String(str);

  return /^-?[.,\d]+$/.test(str) ? Number(_normalizeCommas(str)) : NaN;
}

export function isInteger(n: number) {
  return Math.round(n) === n;
}

export function getPrecision(s: string) {
  const match = s.match(/\.([0-9]*)$/);
  // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
  return match ? match[1].length : 0;
}

export function toPrecision(s: string, precision: number): number | string {
  s = _normalizeLeadingZeros(s);
  const exisingPrecision = getPrecision(s);
  if (exisingPrecision !== precision) {
    const v = parseNumber(s);
    return v.toFixed(precision);
  } else {
    return s;
  }
}

function padDecimals(s: string, n: number) {
  s = _normalizeLeadingZeros(s);
  if (!/\.([0-9]*)$/.test(s)) {
    s += '.';
  }
  const match = s.match(/\.([0-9]*)$/);
  if (match) {
    // @ts-expect-error - TS2532 - Object is possibly 'undefined'.
    const numDecimals = match[1].length;
    const padLength = n - numDecimals;
    if (padLength > 0) {
      s += Array(padLength + 1).join('0');
    }
  }
  return s;
}

export function isSafeNumber(s: string): boolean {
  s = _normalizeCommas(s);

  if (isValidBasicNumber(s)) {
    const n = parseNumber(s);
    const ns = n.toFixed(8);
    return (
      !isNaN(n) &&
      Math.abs(n) <= Number.MAX_SAFE_INTEGER &&
      ns === padDecimals(s, 8) &&
      ns === padDecimals(String(n), 8)
    );
  } else {
    return false;
  }
}

export type NumberConstraintsType = {
  minValue?: number | undefined | null;
  maxValue?: number | undefined | null;
  format?: 'integer' | 'decimal' | undefined | null;
  precision?: number | undefined | null;
  allowNegative?: boolean | undefined | null;
};

export function bindValue(v: number, opts: NumberConstraintsType): number {
  const {minValue, maxValue, format, precision, allowNegative} = opts;

  if (typeof minValue === 'number') {
    v = Math.max(v, minValue);
  }

  if (typeof maxValue === 'number') {
    v = Math.min(v, maxValue);
  }

  if (format === 'integer') {
    v = Math.round(v);
  } else if (format === 'decimal' && typeof precision === 'number') {
    const roundFactor = Math.pow(10, precision);
    v = Math.round(v * roundFactor) / roundFactor;
  }

  if (typeof allowNegative === 'boolean' && !allowNegative && v < 0) {
    v = 0;
  }

  return v;
}

export const wrap = (max: number, n: number) => (max + n) % max;

function _normalizeCommas(s: string) {
  return s.replace(/,/g, '.');
}

function _normalizeLeadingZeros(s: string) {
  return s
    .replace(/^(\-?)0+([1-9])/, '$1$2') // if there's leading zeros before a number, drop all of them
    .replace(/^(\-?)(0)0+/, '$1$2') // if there's more than one leading zero, drop the extras
    .replace(/^(\-?)\./, '$10.'); // if there's no zero before the decimal, add one
}

export const clamp = (lower: number, upper: number, n: number) =>
  Math.min(Math.max(lower, n), upper);

export const byteToHex = (byte: number): string => {
  const hexVal = byte.toString(16);
  return hexVal.length === 1 ? '0' + hexVal : hexVal;
};

export const hexToInt = (hex: string) => parseInt(hex, 16);

export const round = (number: number, precision: number = 3) => {
  const base = Math.pow(10, Math.max(precision, 0));
  return Math.round(number * base) / base;
};

const STEP_PRECISION = 8;

/**
 * Deals with floating point math snafu for stepping values, eg
 * `1.1 + 0.1 === 1.2000000000000002`, while
 * `stepWithFloatingPointPrecision(1.1, 0.1) === 1.2`
 */
export const stepWithFloatingPointPrecision = (
  value: number,
  step: number
): number => {
  return round(value + step, STEP_PRECISION);
};

/**
 * Returns the passed in number or 0 if the number is negative.
 */
export const stayPositive = (n: number) => (n < 0 ? 0 : n);

/**
 * Check is number in range or not
 */

export const inRange = (n: number, range: [number, number]) => {
  if (range.length !== 2) {
    console.error('Range should contain 2 numbers');
    return;
  }

  const [min, max] = range;

  if (max < min) {
    console.error('Max should be larger than min');
    return;
  }

  return n > min && n < max;
};

export const findPivot = (upper: number, lower: number): number => {
  return Math.floor(lower + (upper - lower) / 2);
};

/**
 * Parses a string value to a number and applies validation constraints.
 * Handles comma decimal separators and applies min/max/precision/format constraints.
 *
 * Currently used in on canvas editing of Number at the point of commit.
 * @param value - The string value to parse and constrain
 * @param constraints - Validation constraints to apply
 * @returns The parsed and constrained number, or null if invalid
 */
export function parseNumberWithConstraints(
  value: string,
  constraints: NumberConstraintsType = {}
): number | null {
  const filteredValue = filterNumberText(value);

  if (filteredValue === '' || !isSafeNumber(filteredValue)) {
    return null;
  }

  // Parse the number (handles comma decimal separators)
  const parsedValue = parseNumber(filteredValue);

  if (isNaN(parsedValue)) {
    return null;
  }

  // Apply constraints
  return bindValue(parsedValue, constraints);
}

/**
 * Formats a number value according to constraints, applying bindValue and proper string formatting.
 */
export const formatNumberWithConstraints = (
  value: number,
  constraints: NumberConstraintsType
): string => {
  // Use bindValue to apply all number constraints and rounding
  const boundValue = bindValue(value, constraints);

  // Convert to string - bindValue already handles rounding, we just need string conversion
  if (
    constraints.format === 'decimal' &&
    typeof constraints.precision === 'number' &&
    constraints.precision >= 0
  ) {
    const digits = Math.min(constraints.precision, 8);
    return boundValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
      useGrouping: false,
    });
  } else {
    return boundValue.toLocaleString('en-US', {
      useGrouping: false,
    });
  }
};

// Valid characters for number input
const VALID_NUMBER_CHARS = new Set([
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
  ',',
  '-',
]);

/**
 * Strips invalid characters from text, keeping only digits, separators, and minus signs.
 * Output is does not necessarily produce valid number.
 */
export const removeNonNumberChars = (text: string): string => {
  let hasMinusAtStart = false;

  const finalResult = text
    .split('')
    .reduce<string[]>((acc, char) => {
      if (!VALID_NUMBER_CHARS.has(char)) {
        return acc; // Skip invalid characters
      }

      // Handle minus: only allow if no digits/separators have been added yet
      if (char === '-') {
        if (acc.length === 0 && !hasMinusAtStart) {
          hasMinusAtStart = true;
          acc.push(char);
        }
        // Ignore minus signs that come after digits/separators
        return acc;
      }

      // Add digits and separators
      acc.push(char);
      return acc;
    }, [])
    .join('');

  // Return empty string if no digits found
  return /\d/.test(finalResult) ? finalResult : '';
};

/**
 * Filters a string to create a valid number format by:
 * - Removing invalid characters (not in VALID_NUMBER_CHARS)
 * - Allowing only one decimal separator (. or ,)
 * - Allowing minus sign only at the start
 * - Removing trailing decimal separators
 * - Adding leading zero if result starts with decimal separator
 * - Returning empty string if no digits are found
 *
 * @param text - The input text to filter
 * @returns A filtered string that follows valid number format rules, or empty string if no digits found
 */
export function filterNumberText(text: string): string {
  const cleanedText = removeNonNumberChars(text);

  if (!cleanedText) {
    return '';
  }

  // Process separators to handle thousands vs decimal separators
  const lastDotIndex = cleanedText.lastIndexOf('.');
  const lastCommaIndex = cleanedText.lastIndexOf(',');

  let processedText: string;
  if (lastDotIndex > -1 && lastCommaIndex > -1) {
    // Both comma and dot present
    processedText =
      lastDotIndex > lastCommaIndex
        ? cleanedText.replace(/,/g, '') // "1,234.56" pattern
        : cleanedText.replace(/\./g, ''); // "1.234,56" pattern
  } else {
    // Count occurrences to detect thousands separators
    const dotCount = (cleanedText.match(/\./g) || []).length;
    const commaCount = (cleanedText.match(/,/g) || []).length;

    if (dotCount > 1) {
      processedText = cleanedText.replace(/\./g, '');
    } else if (commaCount > 1) {
      processedText = cleanedText.replace(/,/g, '');
    } else {
      processedText = cleanedText; // Single or no separators
    }
  }

  const withoutTrailingSeparator = processedText.replace(/[.,]$/, '');

  // Add leading zero if needed
  if (
    withoutTrailingSeparator.startsWith('.') ||
    withoutTrailingSeparator.startsWith(',')
  ) {
    return '0' + withoutTrailingSeparator;
  }
  if (
    withoutTrailingSeparator.startsWith('-.') ||
    withoutTrailingSeparator.startsWith('-,')
  ) {
    return '-0' + withoutTrailingSeparator.substring(1);
  }
  return withoutTrailingSeparator;
}
